if (!customElements.get('collection-quick-filters')) {
  customElements.define('collection-quick-filters', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: this.events.signal });
      const mobile = window.matchMedia('(max-width: 767px)');
      const syncSort = () => {
        const sort = document.getElementById('CollectionDrawerSort');
        if (!sort) return;
        sort.disabled = !mobile.matches;
        const value = document.getElementById('SortByBar')?.value;
        sort.querySelectorAll('input').forEach(input => { input.checked = input.value === value; });
      };
      listen(mobile, 'change', () => {
        syncSort();
        if (!mobile.matches) this.querySelectorAll('dialog[open]').forEach(dialog => dialog.close());
      });
      syncSort();
      listen(this, 'click', event => {
        const remove = event.target.closest('[data-quick-remove], .collection-quick-filters__footer a');
        if (remove && typeof FacetFiltersForm !== 'undefined') {
          event.preventDefault();
          const dialog = remove.closest('dialog');
          const selector = dialog ? '[data-quick-open="' + dialog.dataset.quickDialog + '"]' : '[data-quick-all]';
          this.updateResults(new URL(remove.href), selector, dialog);
          return;
        }
        const trigger = event.target.closest('[data-quick-open]');
        if (trigger) {
          this.returnFocus = trigger;
          this.querySelector('[data-quick-dialog="' + trigger.dataset.quickOpen + '"]').showModal();
        }
        const dialog = event.target.closest('dialog');
        if (dialog && event.target.closest('[data-quick-close]')) dialog.close();
        if (dialog && event.target === dialog) {
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
        }
      });
      this.querySelectorAll('dialog').forEach(dialog => {
        listen(dialog, 'close', () => {
          const target = mobile.matches ? this.returnFocus : document.getElementById('Facets-Toggle');
          target?.focus({ preventScroll: true });
        });
      });
      this.querySelectorAll('form').forEach(form => {
        const track = form.querySelector('[data-price-range]');
        if (track) {
          const low = track.querySelector('[data-price-min]');
          const high = track.querySelector('[data-price-max]');
          const fields = [...form.querySelectorAll('input[type="number"]')];
          const ceiling = Math.ceil(Number(high.max));
          low.max = high.max = String(ceiling);
          [low, high].forEach(slider => slider.value = String(Math.round(Number(slider.value))));
          const paint = () => {
            track.style.setProperty('--range-start', (ceiling ? Number(low.value) / ceiling * 100 : 0) + '%');
            track.style.setProperty('--range-end', (ceiling ? Number(high.value) / ceiling * 100 : 100) + '%');
            low.style.zIndex = Number(low.value) >= ceiling ? '3' : '1';
            low.setAttribute('aria-valuemax', high.value);
            high.setAttribute('aria-valuemin', low.value);
          };
          [low, high].forEach((slider, index) => listen(slider, 'input', () => {
            if (index === 0) low.value = Math.min(Number(low.value), Number(high.value));
            else high.value = Math.max(Number(high.value), Number(low.value));
            fields[index].value = String(Math.round(Number(slider.value)));
            fields[index].disabled = false;
            fields.forEach(field => field.setCustomValidity(''));
            paint();
          }));
          fields.forEach((field, index) => listen(field, 'input', () => {
            const value = field.value === '' ? (index === 0 ? 0 : ceiling) : Number(field.value);
            if (!Number.isFinite(value)) return;
            if (index === 0) low.value = Math.max(0, Math.min(value, Number(high.value)));
            else high.value = Math.min(ceiling, Math.max(value, Number(low.value)));
            paint();
          }));
          paint();
        }
        listen(form, 'submit', event => {
          const prices = [...form.querySelectorAll('input[type="number"]')];
          if (prices.length === 2 && prices.every(input => input.value !== '') && Number(prices[0].value) > Number(prices[1].value)) {
            event.preventDefault();
            prices[1].setCustomValidity(form.dataset.priceError);
            prices[1].reportValidity();
            return;
          }
          prices.filter(input => input.value === '').forEach(input => input.disabled = true);
          if (typeof FacetFiltersForm !== 'undefined') {
            event.preventDefault();
            const url = new URL(form.action);
            url.search = new URLSearchParams(new FormData(form)).toString();
            const dialog = form.closest('dialog');
            this.updateResults(url, '[data-quick-open="' + dialog.dataset.quickDialog + '"]', dialog);
          }
        });
        listen(form, 'input', () => form.querySelectorAll('input[type="number"]').forEach(input => input.setCustomValidity('')));
      });
    }
    async updateResults(url, selector, dialog) {
      dialog?.close();
      try {
        await FacetFiltersForm.renderPage(url.search.slice(1));
        const trigger = document.querySelector(selector) || document.querySelector('[data-quick-all]');
        trigger?.focus({ preventScroll: true });
        const rail = trigger?.closest('.collection-quick-filters__rail');
        if (rail) {
          const item = trigger.getBoundingClientRect(), bounds = rail.getBoundingClientRect();
          if (item.right > bounds.right) rail.scrollLeft += item.right - bounds.right + 4;
          else if (item.left < bounds.left) rail.scrollLeft -= bounds.left - item.left + 4;
        }
      } catch {
        location.assign(url.href);
      }
    }
    disconnectedCallback() { this.events?.abort(); }
  });
}

if (!customElements.get('collection-bottom-description')) {
  customElements.define('collection-bottom-description', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const content = this.querySelector('.collection-bottom-description__text');
      const button = this.querySelector('button');
      const measure = () => {
        if (!content.getBoundingClientRect().width) return;
        button.hidden = content.scrollHeight <= parseFloat(getComputedStyle(content).lineHeight) * 5 + 1;
      };
      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(expanded));
        this.dataset.collapsed = String(!expanded);
        button.textContent = expanded ? button.dataset.less : button.dataset.more;
      }, { signal: this.events.signal });
      this.nearby = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.nearby.disconnect();
        this.observer = new ResizeObserver(measure);
        this.observer.observe(content);
        measure();
      }, { rootMargin: '200px' });
      this.nearby.observe(this);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.nearby?.disconnect();
      this.observer?.disconnect();
    }
  });
}
